import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import { useMe } from '@/hooks/use-me';
import type { DeepUnknown } from '@/types/deep-unknown';

import { TimeBasedPlatformStatusSchema } from '../entities/platform-status';

import { getTimeBasedPlatformStatus } from './__mocked_data__/platform-status.mock';

export namespace PlatformStatusQuery {
  export const key = () => ['platform-status', 'platform-status'];

  export type Input = { daysRange: number };

  export type Params = { refetchInterval?: number };

  export const OutputSchema = TimeBasedPlatformStatusSchema;
  export type Output = z.infer<typeof OutputSchema>;

  function outputMapper(
    data: FeApi.PlatformStatusViewOutput,
  ): DeepUnknown<Output> {
    return {
      activeErrorClass: data.active_error_class || undefined,
      platformComponents: data.platform_components.map((component) => ({
        id: component.id,
        name: component.component_name,
        uptime: component.uptime,
        description: component.description || undefined,
        activeErrorClass: component.active_error_class || undefined,
        days: component.days.map((day) => ({
          date: new Date(day.date),
          downtimes: day.downtimes.map((downtime) => ({
            errorClass: downtime.error_class,
            durationSec: downtime.duration_sec,
          })),
        })),
      })),
    };
  }

  function useQueryFn() {
    const platformStatus = useInjectFeApiContext(
      FeApi.maintenanceV1PlatformStatusRetrieve,
    );
    const me = useMe();

    return async function queryFn(input: Input) {
      if (me.config.id === '49') {
        return getTimeBasedPlatformStatus(input.daysRange);
      }

      const result = await ok(platformStatus({ daysRange: input.daysRange }));
      const output = await OutputSchema.parseAsync(outputMapper(result));

      return output;
    };
  }

  export function useQuery(input: Input, params: Params) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(),
      queryFn: () => queryFn(input),
      refetchInterval: params.refetchInterval,
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(),
        queryFn: () => queryFn(input),
      });
    };
  }
}
